import { Component, ViewChild, ElementRef, OnDestroy, OnInit, Input, Inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { VmConnectionService } from './vm-connection.service';
import { VmSessionManagerService, VmSessionData } from '../../../services/vm-session-manager.service';
import { UnifiedVmLauncherService, LaunchContext, VmContextPayload } from '../../../services/unified-vm-launcher.service';
import { IShortLogger, Log } from '../../../services/logger.service';
import { CLASSES_ENTITY_SERVICE_TOKEN } from '../../../injection-tokens/entity-service-tokens';
import { IClassesEntityService } from '../../../interfaces/entities/classes-entity-service.interface';
import { NotificationsService } from '../../../services/home-services/notifications.service';
import { MatSnackBar, MatSnackBarRef, TextOnlySnackBar } from '@angular/material/snack-bar';
import { EMPTY, Subscription, Observable, BehaviorSubject } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { ConfirmDialogService } from '../../../services/confirm-dialog.service';
import { OrgTemplateAadService } from '../../../services/org-template-aad.service';
import { VmConnectionTemplateService } from '../../../services/entity-services/vm-connection-template.service';
import { VmConnectionTemplateDetail } from '../../../interfaces/vm-connection-templates/vm-connection-template.interface';
import { InputStream } from 'guacamole-common-js/lib/InputStream';
import Guacamole from 'guacamole-common-js';
import { DownloadSnackbarComponent } from './download-snackbar-component';

interface ConnectionForm {
  protocol: 'vnc' | 'rdp';
  hostname: string;
  username: string;
  password: string;
}

interface KeyMeta {
  number: number;
  needsShift: boolean;
}

/** Descriptor for a single VM capability shown in the capabilities popover. */
interface CapabilityEntry {
  readonly key: keyof Pick<VmConnectionTemplateDetail,
    'allowCopy' | 'allowPaste' | 'allowFileUpload' | 'allowAudioPlayback' | 'allowBackground'>;
  readonly icon: string;
  readonly label: string;
}

/** Ordered list of capabilities to surface in the VM capabilities popover. */
const VM_CAPABILITIES: ReadonlyArray<CapabilityEntry> = [
  { key: 'allowCopy', icon: 'content_copy', label: 'Copy from VM' },
  { key: 'allowPaste', icon: 'content_paste', label: 'Paste to VM' },
  { key: 'allowFileUpload', icon: 'folder_shared', label: 'File Sharing' },
  { key: 'allowAudioPlayback', icon: 'volume_up', label: 'Audio Playback' },
  { key: 'allowBackground', icon: 'wallpaper', label: 'Desktop Background' },
];

@Component({
  selector: 'app-vm-connection',
  templateUrl: './vm-connection.component.html',
  styleUrls: ['./vm-connection.component.css']
})
export class VmConnectionComponent implements OnInit, OnDestroy {
  private readonly logger: IShortLogger<VmConnectionComponent>
    = Log.create(VmConnectionComponent);

  public readonly AZURE_PORTAL_URL = 'https://portal.azure.com';

  private readonly KEYSYM_MAP: Record<string, KeyMeta> = {
    // Command keys, don't need shift
    'ctrl': { number: 0xFFE3, needsShift: false },
    'alt': { number: 0xFFE9, needsShift: false },
    'del': { number: 0xFFFF, needsShift: false },
    'esc': { number: 0xFF1B, needsShift: false },
    'tab': { number: 0xFF09, needsShift: false },
    'space': { number: 0x0020, needsShift: false },
    'enter': { number: 0xFF0D, needsShift: false },
    'backspace': { number: 0xFF08, needsShift: false },
    // Basic punctuation, don't need shift
    ' ': { number: 0x0020, needsShift: false }, // Space
    ',': { number: 0x002C, needsShift: false },
    '.': { number: 0x002E, needsShift: false },
    '/': { number: 0x002F, needsShift: false },
    ';': { number: 0x003B, needsShift: false },
    '=': { number: 0x003D, needsShift: false },
    '[': { number: 0x005B, needsShift: false },
    ']': { number: 0x005D, needsShift: false },
    '\\': { number: 0x005C, needsShift: false },
    '`': { number: 0x0060, needsShift: false },
    '-': { number: 0x002D, needsShift: false },
    "'": { number: 0x0027, needsShift: false },
    '\n': { number: 0xFF0D, needsShift: false }, // Enter
    '\t': { number: 0xFF09, needsShift: false }, // Tab
    // Special characters, need shift
    '!': { number: 0x0021, needsShift: true },
    '"': { number: 0x0022, needsShift: true },
    '#': { number: 0x0023, needsShift: true },
    '$': { number: 0x0024, needsShift: true },
    '%': { number: 0x0025, needsShift: true },
    '&': { number: 0x0026, needsShift: true },
    '(': { number: 0x0028, needsShift: true },
    ')': { number: 0x0029, needsShift: true },
    '@': { number: 0x0040, needsShift: true },
    '*': { number: 0x002A, needsShift: true },
    '+': { number: 0x002B, needsShift: true },
    ':': { number: 0x003A, needsShift: true },
    '<': { number: 0x003C, needsShift: true },
    '>': { number: 0x003E, needsShift: true },
    '?': { number: 0x003F, needsShift: true },
    '^': { number: 0x005E, needsShift: true },
    '_': { number: 0x005F, needsShift: true },
    '{': { number: 0x007B, needsShift: true },
    '|': { number: 0x007C, needsShift: true },
    '}': { number: 0x007D, needsShift: true },
    '~': { number: 0x007E, needsShift: true },
  };

  @ViewChild('displayContainer', { static: true }) displayContainer!: ElementRef;

  // Input for production use - session key from route or parent component
  @Input() sessionKey?: string;
  @Input() isTestMode: boolean = true; // Toggle between test and production mode

  // Form model (used in test mode)
  connectionForm: ConnectionForm = {
    protocol: 'vnc',
    hostname: '',
    username: '',
    password: ''
  };

  // Component state
  isConnecting = false;
  isConnected = false;
  connectionError: string | null = null;
  /** Machine-readable code that drives the error panel variant. */
  errorCode: 'server'
    | 'building' | 'not-provisioned' | 'no-session'
    | 'session-expired' | 'generic' | 'incomplete-session-details'
    | null = null;

  sidebarVisible = false;
  isFullscreen = false;
  isContentReady = false;

  // Hold key functionality
  heldKey: string | null = null;
  holdInterval: any = null;

  // Keystroke queue management
  isSendingKeystrokes: boolean = false;

  // Duplicate session protection
  isDuplicateSession: boolean = false;
  duplicateSessionError: string = '';

  /**
   * Opaque session GUID returned by POST /api/v1/launch/prepare.
   * Sent via navigator.sendBeacon on tab close so the backend marks the
   * session inactive and the next launch attempt is not blocked.
   */
  private activeTemplateSessionId: string | null = null;

  /**
   * The SHA key from the ?key= query parameter, retained so that
   * onFocusLossReconnect can re-dispatch a fresh backend fetch instead of
   * reading stale credentials from localStorage.
   */
  private contextKey: string | null = null;

  // Optimal resolution calculated from screen size
  optimalResolution = { width: 1280, height: 720 };
  systemResolution = { width: 0, height: 0 };
  private outsideClickListener?: (event: Event) => void;
  autoCloseCountdown: number = 60;

  isPerformingPreConnectionSync: boolean = true;

  // ---------------------------------------------------------------------------
  // Azure AD credentials (populated when the backend provisions a UA account)
  // ---------------------------------------------------------------------------
  /** The Azure AD user principal name (UPN) associated with this session. */
  aadUserPrincipalName: string | null = null;
  /** The ID of the VM connection template associated with this session. */
  vmConnectionTemplateId: string | null | undefined;

  /** The local part of the UPN (before '@') used as the display value in the field. */
  get aadUpnLocalPart(): string {
    if (!this.aadUserPrincipalName) return '';
    return this.aadUserPrincipalName.split('@')[0];
  }

  aadTemporaryAccessPass: string | null = null;
  aadTapRevealed = false;
  hasAadCredentials = false;
  /** True while a TAP regeneration call is in-flight. */
  isRegeneratingTap = false;
  /** Visibility toggle for VM Account password. */
  vmPasswordRevealed = false;

  // ---------------------------------------------------------------------------
  // VM connection template capabilities
  // ---------------------------------------------------------------------------
  // VM connection template capabilities
  // ---------------------------------------------------------------------------
  /** Full template detail loaded from the backend; null if no template is associated. */
  vmCaps: VmConnectionTemplateDetail | null = null;

  /**
   * Whether the template has recording enabled at all.
   * Raw flag from the backend — does not account for context-based suppression.
   */
  isScreenRecording = false;

  /**
   * Explicit opt-in override for trainer/admin launch contexts
   * (`class-template-vm`, `org-template-vm`).
   * Recording is suppressed by default for those contexts even when the template
   * enables it; this toggle lets them turn it on intentionally.
   * Stub only — the backend wiring is a separate follow-up.
   */
  recordingOverrideEnabled = false;

  /**
   * Whether recording is actually active for this session, accounting for
   * the launch-context rules:
   * - `participant-self-vm`: active when template flag is on.
   * - `operator-remote-vm`: active when template flag is on (joins participant's
   *   existing connection; recording is on participant's side — indicator only).
   * - `class-template-vm` / `org-template-vm`: suppressed by default; active only
   *   when the user explicitly enables `recordingOverrideEnabled`.
   */
  get isRecordingActive(): boolean {
    if (!this.isScreenRecording) return false;
    const ctx = this._launchContext;
    if (ctx === 'class-template-vm' || ctx === 'org-template-vm') {
      return this.recordingOverrideEnabled;
    }
    return true;
  }

  /**
   * Whether to show the recording opt-in toggle in the drawer.
   * Only relevant for trainer/admin contexts when the template has recording enabled.
   */
  get showRecordingToggle(): boolean {
    const ctx = this._launchContext;
    return this.isScreenRecording &&
      (ctx === 'class-template-vm' || ctx === 'org-template-vm');
  }
  /** Whether the in-drawer VM capabilities popover is open. */
  capsPopoverOpen = false;
  /** Timer handle used to delay closing the popover so the mouse can move from button to popover. */
  private _capsHideTimer: ReturnType<typeof setTimeout> | null = null;

  /** Returns the subset of VM_CAPABILITIES that are enabled on this template. */
  get vmCapsAllowed(): ReadonlyArray<CapabilityEntry> {
    const caps = this.vmCaps;
    if (!caps) return [];
    return VM_CAPABILITIES.filter(c => caps[c.key]);
  }

  /** Returns the subset of VM_CAPABILITIES that are disabled on this template. */
  get vmCapsDenied(): ReadonlyArray<CapabilityEntry> {
    const caps = this.vmCaps;
    if (!caps) return [];
    return VM_CAPABILITIES.filter(c => !caps[c.key]);
  }

  /** Whether any capabilities are attached to the current VM template. */
  get hasCapabilitiesAttached(): boolean {
    // Capability attachment gate is -- vmConnectionTemplateId must not be null,
    // And vmCaps should also not be null.
    return !!this.vmConnectionTemplateId && !!this.vmCaps;
  }

  /**
   * True when clipboard access is relevant to this session — i.e. the template
   * permits at least one direction of clipboard transfer (copy from VM or paste to VM).
   * When false the "Clipboard Access" re-permission button is hidden entirely.
   */
  get isClipboardRelevant(): boolean {
    return this.hasCapabilitiesAttached && (this.vmCaps!.allowCopy || this.vmCaps!.allowPaste);
  }

  /**
   * Opens the VM capabilities popover immediately, cancelling any pending close timer.
   * Called on `mouseenter` of the trigger button or the popover itself.
   */
  openCapsPopover(): void {
    if (this._capsHideTimer !== null) {
      clearTimeout(this._capsHideTimer);
      this._capsHideTimer = null;
    }
    this.capsPopoverOpen = true;
  }

  /**
   * Schedules the popover to close after a short delay.
   * Delay gives the mouse time to travel from the trigger button into the popover
   * without the popover flashing closed.
   * Called on `mouseleave` of the trigger button or the popover itself.
   */
  closeCapsPopoverDelayed(): void {
    this._capsHideTimer = setTimeout(() => {
      this.capsPopoverOpen = false;
      this._capsHideTimer = null;
    }, 150);
  }

  // ---------------------------------------------------------------------------
  // Reconnect / connection-health state
  // ---------------------------------------------------------------------------
  /** True while the GuacamoleClientInstance is actively retrying the connection. */
  isReconnecting = false;
  /** Current reconnect attempt number shown in the reconnecting banner. */
  reconnectAttemptDisplay = 0;
  /** Maximum reconnect attempts exposed for the template. */
  reconnectMaxAttempts = 5;

  // ---------------------------------------------------------------------------
  // Window-level event handler references (kept so they can be removed cleanly)
  // ---------------------------------------------------------------------------
  private windowResizeHandler?: () => void;
  private visibilityChangeHandler?: () => void;
  private resizeDebounceTimer: any = null;
  private readonly RESIZE_DEBOUNCE_MS = 400;

  // ---------------------------------------------------------------------------
  // Focus-loss modal state
  // ---------------------------------------------------------------------------
  /** Phase 1: silent delay before showing the modal (ms) — overridable via environment. */
  private readonly FOCUS_LOSS_DETECTION_MS = environment.focusLossDetectionMs ?? 30_000;

  /** Pause (ms) before reconnecting to allow the backend to expire the old session. */
  private readonly PREPARE_VM_DELAY_MS = environment.prepareVmDelayMs ?? 1000;
  // Phase 2 auto-disconnect removed — see crit_09_focus_loss_session_claim.md
  /** True while the Material focus-loss dialog is open (guards against re-entry). */
  focusLossDialogOpen = false;
  /** Phase 1: single-shot timeout that fires once focus has been lost long enough to show the modal. */
  private focusLossPhase1TimerId: ReturnType<typeof setTimeout> | null = null;

  /** Guacamole session identity — set in connectWithData, used by keystroke helpers and disconnect. */
  private _guacClassId: string | null = null;
  private _guacUserId: string | null = null;

  // ── AAD context fields ──────────────────────────────────────────────────────
  private _uaTemplateId: string | null = null;
  private _launchContext: LaunchContext = 'participant-self-vm';
  isOrgTemplateContext = false;
  isFileTransferAllowed = false;

  // ── Thumbnail capture state ──────────────────────────────────────────────────
  private thumbnailIntervalId: ReturnType<typeof setInterval> | null = null;
  private readonly THUMBNAIL_INTERVAL_MS = 5_000;
  private thumbnailClassId: string | null = null;
  private thumbnailUserId: string | null = null;
  private thumbnailContext: string | null = null;

  // ── Trainer monitoring indicator ─────────────────────────────────────────────
  /** True while a trainer is actively monitoring this participant's VM session. */
  isBeingMonitored = false;
  private monitoringSubscriptions: Subscription[] = [];

  constructor(
    private vmConnectionService: VmConnectionService,
    private route: ActivatedRoute,
    private sessionManager: VmSessionManagerService,
    private unifiedLauncher: UnifiedVmLauncherService,
    private notificationsService: NotificationsService,
    private confirmDialogService: ConfirmDialogService,
    private snackBar: MatSnackBar,
    @Inject(CLASSES_ENTITY_SERVICE_TOKEN) private classesService: IClassesEntityService,
    private orgTemplateAadService: OrgTemplateAadService,
    private readonly vmConnectionTemplateService: VmConnectionTemplateService
  ) {
    // Set up browser close/refresh protection
    this.setupBeforeUnloadHandler();
  }

  /**
   * Set up browser beforeunload handler to show confirmation when window is closed/refreshed
   */
  private setupBeforeUnloadHandler(): void {
    window.addEventListener('beforeunload', this.handleBeforeUnload);
    window.addEventListener('unload', this.handleUnload);
  }

  /**
   * Handle browser close/refresh - show confirmation only (no cleanup yet)
   */
  private handleBeforeUnload = (event: BeforeUnloadEvent): string | void => {
    // Only show confirmation if we have an active VM connection
    if (this.isConnected) {
      this.logger.i('Before unload - showing confirmation dialog');
      event.preventDefault();
      return;
    }
  }

  /**
   * Perform cleanup when window is being closed
   * This runs regardless of user choice in beforeunload dialog
   * Note: Does NOT clear session data to allow reconnect on page reload
   */
  private performCleanupOnExit(): void {
    try {
      if (this.isConnected && this.sessionKey) {
        this.logger.i('Window closing - performing VM cleanup');
        this.markTemplateSessionInactive();

        // Stop any ongoing operations
        this.stopHoldingKey();
        this.removeOutsideClickListener();
        this.disconnectActiveClient();

        // DO NOT clear session data here - allows user to reload/reconnect
        // Session is only cleared on intentional disconnect (confirmDisconnect)

        this.isConnected = false;
        this.isContentReady = false;
      }
    } catch (error) {
      this.logger.e('Error during exit cleanup:', error);
    }
  }

  /**
   * Handle actual window unload - perform cleanup when window is definitely closing
   */
  private handleUnload = (): void => {
    this.logger.i('Window is actually unloading - performing VM cleanup');
    this.performCleanupOnExit();
  }

  /**
   * Remove beforeunload and unload event listeners (used during normal disconnect)
   */
  private removeBeforeUnloadHandler(): void {
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
    window.removeEventListener('unload', this.handleUnload);
  }

  /** Always show a short preparing overlay before backend connection fetches. */
  private async showPreparingVmOverlay(): Promise<void> {
    this.isPerformingPreConnectionSync = true;
    await new Promise<void>(resolve => setTimeout(resolve, this.PREPARE_VM_DELAY_MS));
    this.isPerformingPreConnectionSync = false;
  }

  /** Best-effort signal to close any active template launch-session record on the backend. */
  private markTemplateSessionInactive(): void {
    if (!this.activeTemplateSessionId) return;
    const payload = JSON.stringify({ sessionId: this.activeTemplateSessionId });
    navigator.sendBeacon(
      '/api/v1/launch/session/close',
      new Blob([payload], { type: 'application/json' })
    );
    this.activeTemplateSessionId = null;
  }

  /** Disconnect the current Guacamole client using the resolved session identity. */
  private disconnectActiveClient(): void {
    if (this.isTestMode) {
      this.vmConnectionService.disconnectTestVm();
      return;
    }

    if (this._guacClassId && this._guacUserId) {
      this.vmConnectionService.disconnectProductionVm(this._guacClassId, this._guacUserId);
    }
  }

  /**
   * Clear session data from localStorage
   * Parses sessionKey and removes cached session to prevent stale data reuse
   */
  private clearSessionData(): void {
    if (this._guacClassId && this._guacUserId) {
      this.sessionManager.clearSession(this._guacClassId, this._guacUserId);
      this.logger.i(`Cleared session data: ${this._guacClassId}/${this._guacUserId}`);
    }
  }

  /**
   * Angular lifecycle — resolve session key and connect.
   *
   * Session claim / BroadcastChannel deduplication has been removed (crit_09).
   * Duplicate-session enforcement is handled server-side; any rejection surfaces
   * as a Guacamole connection error, which the existing error-handling renders.
   */
  async ngOnInit(): Promise<void> {
    // SHA-keyed path (new): single `?key=` parameter — no sensitive context in URL.
    const key = this.route.snapshot.queryParamMap.get('key');
    if (key) {
      this.contextKey = key;
      await this.connectViaContextKey(key);
      return;
    }

    this.logger.i('No SHA context key provided — running in test mode');
    this.isPerformingPreConnectionSync = false;
  }

  /**
   * Show duplicate session error message with clean user-friendly text
   */
  private showDuplicateSessionError(claimResult?: any): void {
    this.isDuplicateSession = true;

    // Always show clean, user-friendly message regardless of technical details
    this.duplicateSessionError = 'This VM session is already active in another tab or window. Please close the other window first or use that tab to continue your session.';

    // Log technical details to console for debugging (not shown to user)
    if (claimResult?.conflictDetails) {
      const timeDiff = Math.abs(claimResult.conflictDetails.timestampDiff);
      const timeStr = timeDiff > 1000 ? `${(timeDiff / 1000).toFixed(1)}s` : `${timeDiff}ms`;

      this.logger.i(`Duplicate session debug info:`, {
        conflictingTab: claimResult.conflictDetails.existingTabId,
        timeDifference: timeStr,
        reason: claimResult.reason,
        rawTimeDiff: timeDiff
      });
    }

    this.isTestMode = false; // Hide the connection form
    this.isConnecting = false;
    this.isConnected = false;

    // Auto-close countdown
    let intervalId: number = window.setInterval(() => {
      this.autoCloseCountdown--;
      if (this.autoCloseCountdown <= 0) {
        window.clearInterval(intervalId);
        this.closeCurrentTab();
      }
    }, 1000);

    this.logger.e('VM session claim rejected:', this.sessionKey, claimResult);
  }

  /**
   * Sets `connectionError` and `errorCode` from any thrown value.
   * Strips HTTP internals (URL, status text) so no implementation details
   * reach the user; logs the full error for debugging.
   */
  private setConnectionError(error: any, context: string): void {
    this.logger.e(`${context} failed:`, error);
    const status: number | undefined = error?.status;

    if (status === 500 || status === 503) {
      this.errorCode = 'server';
      this.connectionError = 'The server encountered an unexpected error. Please try again shortly.';
    } else if (status === 404) {
      this.errorCode = 'not-provisioned';
      this.connectionError = 'The VM for this session could not be found. It may not have been provisioned yet.';
    } else if (status === 410) {
      this.errorCode = 'not-provisioned';
      this.connectionError = 'The template VM has not been provisioned yet. Please create the template VM first in Class Settings.';
    } else if (status === 202) {
      this.errorCode = 'building';
      this.connectionError = 'Your lab environment is still being set up. Please wait a few minutes and try again.';
    } else if (status === 401 || status === 403) {
      this.errorCode = 'session-expired';
      this.connectionError = 'Your session has expired. Please close this window and log in again.';
    } else {
      // Generic — no raw message, URL, or status text exposed to the user.
      this.errorCode = 'generic';
      this.connectionError = 'Unable to establish the VM connection. Please close this window and try again.';
    }
  }

  /**
   * Close current tab (called from duplicate session error)
   */
  closeCurrentTab(): void {
    this.logger.i('Closing current tab due to duplicate session');
    this.removeBeforeUnloadHandler();
    window.close();
  }

  /**
   * New flow: connect via SHA-keyed context payload stored in localStorage.
   * Reads `vm_ctx_{key}`, parses the payload, and dispatches to the appropriate
   * connection method based on mode and context.
   */
  private async connectViaContextKey(key: string): Promise<void> {
    const payloadStr = localStorage.getItem(`vm_ctx_${key}`);
    if (!payloadStr) {
      this.errorCode = 'session-expired';
      this.connectionError = 'This session link has expired. Please close this window and try again.';
      this.isPerformingPreConnectionSync = false;
      return;
    }

    let payload: VmContextPayload;
    try {
      payload = JSON.parse(payloadStr);
    } catch {
      this.errorCode = 'generic';
      this.connectionError = 'Invalid session context. Please close this window and try again.';
      this.isPerformingPreConnectionSync = false;
      return;
    }

    this.sessionKey = payload.sessionKey;
    this.isTestMode = false;
    this._launchContext = payload.context;
    this._uaTemplateId = localStorage.getItem(`vm_ua_tpl_${key}`);
    this.isOrgTemplateContext = payload.context === 'org-template-vm';
    this.logger.i(`VM Component (SHA key): context=${payload.context} sessionKey=${this.sessionKey}`);

    // Load VM connection template capabilities (file transfer, screen recording, etc.)
    this.vmConnectionTemplateId = payload.vmConnectionTemplateId;
    if (this.vmConnectionTemplateId) {
      this.loadTemplateCapabilities(this.vmConnectionTemplateId);
    }

    await this.showPreparingVmOverlay();

    // Backend is the single source of truth for all contexts — always dispatch
    // to the appropriate endpoint regardless of the stored mode.  This closes
    // the localStorage-tampering shortcut for every launch context: editing
    // mode to 'cached' in DevTools has no effect.
    await this.dispatchFetchContext(payload);
  }

  /**
   * Dispatches to the correct backend-fetch method based on the context in the payload.
   */
  private async dispatchFetchContext(payload: VmContextPayload): Promise<void> {
    const context = payload.context;

    if (context === 'org-template-vm' || context === 'class-template-vm') {
      await this.connectViaTemplateLaunch(payload);
      return;
    }

    // participant-self-vm and operator-remote-vm
    await this.connectViaBackend(payload);
  }

  /**
   * Fetch VM connection template capabilities.
   * Updates file transfer policy, screen recording flag, and full caps object.
   * On error, defaults to safe/open values.
   */
  private loadTemplateCapabilities(templateId: string): void {
    this.vmConnectionTemplateService.getById(templateId).subscribe({
      next: (detail) => {
        this.vmCaps = detail;
        this.isFileTransferAllowed = detail?.allowFileUpload ?? false;
        this.isScreenRecording = detail?.allowScreenRecording ?? false;
        this.logger.i(`Template capabilities loaded: fileTransfer=${this.isFileTransferAllowed}, screenRecording=${this.isScreenRecording}`);
      },
      error: (err) => {
        this.logger.w(`Failed to load VM connection template '${templateId}' — using defaults`, err);
        this.isFileTransferAllowed = false; // Default to safest option
        this.isScreenRecording = false;
      }
    });
  }

  /**
   * Template launch path: calls the deduplication-aware POST /api/v1/launch/prepare
   * endpoint, then connects using the returned credentials.
   */
  private async connectViaTemplateLaunch(payload: VmContextPayload): Promise<void> {
    const templateId = payload.templateId ?? '';
    const classId = payload.classId ?? null;
    const userId = payload.userId ?? '';
    const clientSha = payload.sessionSha ?? '';

    if (!templateId || !userId || !clientSha) {
      this.connectionError = 'Invalid template launch context. Please relaunch from the class portal.';
      return;
    }

    this.isConnecting = true;
    this.connectionError = null;
    this.errorCode = null;

    try {
      const launchDetails: any = await this.classesService.prepareLaunchContext({
        clientSha,
        context: payload.context,
        userId,
        templateId,
        classId,
        userName: payload.userName ?? 'Template Admin',
        vmConnectionTemplateId: payload.vmConnectionTemplateId ?? null,
      }).toPromise();

      if (!launchDetails?.encryptedToken || !launchDetails?.cloudUserName || !launchDetails?.password) {
        this.connectionError = 'Incomplete session details received from server. Please try again.';
        return;
      }

      // Store the session GUID so we can mark the backend session inactive on tab close.
      if (launchDetails.sessionId) {
        this.activeTemplateSessionId = launchDetails.sessionId;
      }

      const sessionData: VmSessionData = {
        encryptedToken: launchDetails.encryptedToken,
        cloudUserName: launchDetails.cloudUserName,
        password: launchDetails.password,
        classId: classId ?? templateId,
        userId,
        protocol: launchDetails.protocol,
        createdAt: new Date(),
        launchContext: payload.context,
        ...(launchDetails.templateId && { templateId: launchDetails.templateId }),
        ...(launchDetails.resourceId && { resourceId: launchDetails.resourceId }),
        ...(launchDetails.hostname && { hostname: launchDetails.hostname }),
      };

      // Populate AAD credentials when the backend has provisioned a UA account.
      this.aadUserPrincipalName = launchDetails.aadUserPrincipalName ?? null;
      this.aadTemporaryAccessPass = launchDetails.aadTemporaryAccessPass ?? null;
      this.hasAadCredentials = !!(this.aadUserPrincipalName && this.aadTemporaryAccessPass);

      await this.connectWithData(classId ?? templateId, userId, sessionData, payload.context);

    } catch (error: any) {
      if (error?.status === 409) {
        this.showDuplicateSessionError();
        return;
      }
      this.setConnectionError(error, 'connectViaTemplateLaunch');
    } finally {
      this.isConnecting = false;
      if (!this.isConnected) {
        this.isContentReady = false;
      }
    }
  }

  /**
   * Fetches launch credentials/token from POST /api/v1/launch/prepare
   * for participant-self-vm and operator-remote-vm contexts.
   */
  private async connectViaBackend(payload: VmContextPayload): Promise<void> {
    const classId = payload.classId ?? '';
    const userId = payload.userId ?? '';
    const clientSha = payload.sessionSha ?? '';

    if (!classId || !userId || !clientSha) {
      this.connectionError = 'Invalid session context. Please relaunch from the class portal.';
      this.isPerformingPreConnectionSync = false;
      return;
    }

    this.isConnecting = true;
    this.connectionError = null;

    try {
      const launchDetails: any = await this.classesService.prepareLaunchContext({
        clientSha,
        context: payload.context,
        userId,
        classId,
        studentUserId: payload.studentUserId ?? null,
        vmId: payload.vmId ?? null,
        userName: payload.userName ?? '',
        vmConnectionTemplateId: payload.vmConnectionTemplateId ?? null,
      }).toPromise();

      if (launchDetails?.status === 'building') {
        this.errorCode = 'building';
        this.connectionError = 'Your lab environment is still being set up. Please wait a few minutes and try again.';
        return;
      }
      if (launchDetails?.status === 'wait-for-creation') {
        this.errorCode = 'not-provisioned';
        this.connectionError = 'The template VM is not ready yet. Please try again shortly.';
        return;
      }
      if (!launchDetails?.encryptedToken) {
        this.errorCode = 'incomplete-session-details';
        this.connectionError = 'Incomplete session details received from server. Please try again.';
        return;
      }

      // Store the session GUID so we can mark the backend session inactive on tab close.
      if (launchDetails.sessionId) {
        this.activeTemplateSessionId = launchDetails.sessionId;
      }

      const isJoinFlow = launchDetails?.protocol === 'join';

      const sessionData: VmSessionData = {
        encryptedToken: launchDetails.encryptedToken,
        cloudUserName: launchDetails.cloudUserName ?? launchDetails.userName ?? payload.userName ?? 'VM User',
        password: launchDetails.password ?? '',
        classId,
        userId,
        protocol: isJoinFlow ? 'rdp' : launchDetails.protocol,
        createdAt: new Date(),
        launchContext: payload.context,
        ...(launchDetails.templateId && { templateId: launchDetails.templateId }),
        ...(launchDetails.resourceId && { resourceId: launchDetails.resourceId }),
        ...(launchDetails.hostname && { hostname: launchDetails.hostname }),
      };

      // Populate AAD credentials when the backend has provisioned a UA account.
      this.aadUserPrincipalName = launchDetails.aadUserPrincipalName ?? null;
      this.aadTemporaryAccessPass = launchDetails.aadTemporaryAccessPass ?? null;
      this.hasAadCredentials = !!(this.aadUserPrincipalName && this.aadTemporaryAccessPass);

      // Belt-and-suspenders: if the localStorage payload had a stale null template ID
      // (e.g. student view didn't include it at launch time), use the server-resolved value
      // from the launch response to gate file transfer correctly.
      const serverTemplateId: string | null = launchDetails?.effectiveVmConnectionTemplateId ?? null;
      if (serverTemplateId && !this.vmConnectionTemplateId) {
        this.vmConnectionTemplateId = serverTemplateId;
        this.loadTemplateCapabilities(serverTemplateId);
      }

      await this.connectWithData(classId, userId, sessionData, payload.context);

    } catch (error: any) {
      if (error?.status === 409) {
        this.showDuplicateSessionError();
        return;
      }
      if (error?.status === 404) {
        this.errorCode = 'no-session';
        this.connectionError = 'The student is not currently connected. You can only join an active session.';
        return;
      }
      this.setConnectionError(error, 'connectViaBackend');
    } finally {
      this.isConnecting = false;
      if (!this.isConnected) {
        this.isContentReady = false;
      }
    }
  }

  /**
   * Connect to the VM using pre-fetched sessionData (no localStorage).
   * Called by connectViaBackend() after successfully obtaining credentials.
   */
  private async connectWithData(
    classId: string,
    userId: string,
    sessionData: VmSessionData,
    context: LaunchContext
  ): Promise<void> {
    // Persist the Guacamole session identity so keystroke and disconnect helpers
    // can look up the correct client without splitting the SHA-keyed sessionKey.
    this._guacClassId = classId;
    this._guacUserId = userId;

    this.connectionForm = {
      protocol: sessionData.protocol || 'rdp',
      hostname: 'Connected via session',
      username: sessionData.cloudUserName,
      password: sessionData.password
    };

    this.calculateOptimalResolution();

    const display = document.getElementById('display');
    if (display) {
      while (display.firstChild) {
        display.removeChild(display.firstChild);
      }
    }

    const displayDiv = document.getElementById('display');
    await this.vmConnectionService.connectWithProvidedSessionData(
      classId, userId, sessionData, displayDiv!, this.optimalResolution
    );

    this.isConnected = true;

    this.setupOutsideClickListener();
    this.setupResizeHandler();
    this.setupVisibilityHandlers();

    if (context === 'participant-self-vm') {
      this.thumbnailClassId = classId;
      this.thumbnailUserId = userId;
      this.thumbnailContext = context;
      this.uploadThumbnail();
      this.startThumbnailCapture();
      this.subscribeToMonitoringEvents(classId);
    }

    this.logger.i('✅ Backend-fetched session connection established');
    await this.waitForContentToRender();
    this.isContentReady = true;
    // Fit the rendered display to the current viewport — handles small-screen launches.
    this.scaleDisplayToViewport();
    this.setupConnectionCallbacks(classId, userId);
  }

  /**
   * Shared quit path — force-disconnects Guac, removes handlers, then closes.
   * Called from the drawer Disconnect button AND the focus-loss modal Quit action.
   */
  performQuit(): void {
    try { this.markTemplateSessionInactive(); } catch { /* best-effort */ }
    try { this.disconnectActiveClient(); } catch { /* best-effort */ }
    this.removeBeforeUnloadHandler();
    window.close();
    setTimeout(() => { window.location.href = '/'; }, 300);
  }

  /**
   * Disconnect from VM — opens a confirmation dialog then calls performQuit.
   */
  disconnect(): void {
    this.confirmDialogService.confirm({
      title: 'Disconnect from VM',
      message: 'Are you sure you want to disconnect from the VM and close this window?',
      type: 'warning',
      confirmText: 'Disconnect',
      cancelText: 'Cancel',
      cancelLoadingDurationMs: 2000,
      disableClose: false,
    }).subscribe(confirmed => {
      if (confirmed) this.confirmDisconnect();
    });
  }

  /**
   * Reconnect from the drawer — re-uses the same focus-loss reconnect path.
   */
  drawerReconnect(): void {
    this.confirmDialogService.confirm({
      title: 'Reconnect to VM',
      message: 'This will close the current session and open a fresh connection to the VM.',
      type: 'info',
      confirmText: 'Reconnect',
      cancelText: 'Cancel',
    }).subscribe(confirmed => {
      if (confirmed) this.onFocusLossReconnect();
    });
  }

  /** Toggle browser fullscreen (F11 equivalent). */
  toggleFullscreen(): void {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().then(() => { this.isFullscreen = true; }).catch(() => { });
    } else {
      document.exitFullscreen().then(() => { this.isFullscreen = false; }).catch(() => { });
    }
  }

  /** Tracks whether the browser currently grants us clipboard-read access. */
  clipboardPermissionState: 'granted' | 'denied' | 'prompt' | 'unknown' = 'unknown';

  /** True while a permission prompt is in flight, so we can disable the trigger button. */
  isRequestingClipboardPermission = false;

  /**
   * Re-trigger the browser's clipboard permission prompt. This exists because the
   * Ctrl+V keydown bridge (in {@link GuacamoleClientInstance}) relies on
   * `navigator.clipboard.readText()` to fetch the local clipboard when the canvas owns
   * focus — but if the user previously clicked "Block" on the implicit prompt the API
   * silently rejects every call afterwards. Calling `readText()` from a fresh user
   * gesture (this button click) is one of the few ways to surface the browser's UI
   * again so the user can flip the decision.
   *
   * The actual clipboard payload is intentionally discarded — we do not want to leak
   * unrelated clipboard contents into the VM here; only re-establish the permission so
   * subsequent paste shortcuts work.
   */
  requestClipboardPermission(): void {
    if (this.isRequestingClipboardPermission) return;
    if (!navigator.clipboard || typeof navigator.clipboard.readText !== 'function') {
      this.logger.w('Clipboard API not available in this browser/context.');
      return;
    }
    this.isRequestingClipboardPermission = true;

    navigator.clipboard.readText()
      .then(_text => {
        this.clipboardPermissionState = 'granted';
        this.logger.i('Clipboard read permission granted.');
      })
      .catch(err => {
        this.clipboardPermissionState = 'denied';
        this.logger.w('Clipboard read permission still denied — the user must update site permissions in the browser settings.', err);
      })
      .finally(() => {
        this.isRequestingClipboardPermission = false;
      });
  }

  /**
   * Probe the current clipboard-read permission state without prompting the user.
   * Used to decide whether to even render the "Request clipboard access" affordance:
   * if the browser already says `granted` the button is hidden as redundant noise.
   */
  refreshClipboardPermissionState(): void {
    if (!navigator.permissions || typeof navigator.permissions.query !== 'function') {
      this.clipboardPermissionState = 'unknown';
      return;
    }
    // `clipboard-read` is a draft permission name; some browsers (Firefox) reject the
    // query entirely. Both rejection and "missing" are treated as `unknown` so we still
    // render the button — pressing it triggers a real prompt where one is supported.
    try {
      navigator.permissions.query({ name: 'clipboard-read' as PermissionName })
        .then(status => {
          this.clipboardPermissionState = status.state as 'granted' | 'denied' | 'prompt';
        })
        .catch(() => { this.clipboardPermissionState = 'unknown'; });
    } catch {
      this.clipboardPermissionState = 'unknown';
    }
  }

  /**
   * Perform the actual disconnect after confirmation.
   */
  confirmDisconnect(): void {

    try {
      this.markTemplateSessionInactive();

      // Remove beforeunload handler since this is intentional disconnect
      this.removeBeforeUnloadHandler();
      this.removeOutsideClickListener();
      this.removeWindowEventListeners();
      if (this.resizeDebounceTimer) clearTimeout(this.resizeDebounceTimer);
      this.stopHoldingKey(); // Stop any held keys

      this.disconnectActiveClient();

      // Clear session data from localStorage
      this.clearSessionData();

      this.isConnected = false;
      this.isContentReady = false;
      this.connectionError = null;

      // Clear display container
      const display = document.getElementById('display');
      if (display) {
        while (display.firstChild) {
          display.removeChild(display.firstChild);
        }
      }

      this.logger.i(this.isTestMode ? 'Test connection disconnected - closing window' : 'Connection disconnected - closing window');

      // Close the window after successful disconnect
      setTimeout(() => {
        window.close();
      }, 500); // Small delay to ensure cleanup completes

    } catch (error) {
      this.logger.e('Error disconnecting connection:', error);
      // Still try to close window even if there's an error
      setTimeout(() => {
        window.close();
      }, 500);
    }
  }

  /**
   * Validate form data
   */
  private validateForm(): boolean {
    if (!this.connectionForm.hostname.trim()) {
      this.connectionError = 'Hostname is required';
      return false;
    }
    if (!this.connectionForm.username.trim()) {
      this.connectionError = 'Username is required';
      return false;
    }
    if (!this.connectionForm.password.trim()) {
      this.connectionError = 'Password is required';
      return false;
    }
    return true;
  }

  /**
   * Toggle the VM-details drawer open/close.
   * sidebarVisible drives the [opened] binding on mat-sidenav.
   */
  toggleSidebar(): void {
    this.sidebarVisible = !this.sidebarVisible;
  }

  /**
   * Called by (openedChange) so that closing the drawer via the backdrop
   * keeps sidebarVisible in sync.
   */
  onDrawerOpenedChange(opened: boolean): void {
    this.sidebarVisible = opened;
  }

  /**
   * Opens the browser file-picker and uploads the selected file(s) to the VM
   * via Guacamole's file-stream mechanism. Called from the "Upload File" button
   * in the VM Details drawer.
   *
   * Accepts any number of files — each is queued through
   * {@link GuacamoleClientInstance#uploadFile} independently so multi-file
   * uploads are safe.
   *
   * @param event - Change event from the hidden `<input type="file">` element
   */
  onFileInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;

    if (!input.files?.length || !this._guacClassId || !this._guacUserId) return;

    const instance = this.vmConnectionService.getClientInstance(this._guacClassId, this._guacUserId);
    if (!instance) {
      this.snackBar.open('No active VM session — please reconnect first.', 'OK', {
        duration: 5000, panelClass: ['snack-error']
      });
      return;
    }

    Array.from(input.files).forEach(file => instance.uploadFile(file));

    // Reset so the same file can be selected again in a subsequent picker open.
    input.value = '';
  }

  // ── Error-panel computed helpers ─────────────────────────────────────────

  get errorTitle(): string {
    const titles: Record<string, string> = {
      server: 'Server Error',
      building: 'Environment Being Prepared',
      'not-provisioned': 'VM Not Available',
      'no-session': 'Student Not Connected',
      'session-expired': 'Session Expired',
      'incomplete-session-details': 'Session Configuration Incomplete',
      generic: 'Connection Failed',
    };
    return titles[this.errorCode ?? 'generic'] ?? 'Connection Failed';
  }

  get errorHint(): string {
    const hints: Record<string, string> = {
      server: 'If this problem persists please contact your administrator.',
      building: 'This may take a few minutes. Close this tab and try again shortly.',
      'not-provisioned': 'Contact your instructor or administrator if you believe this is an error.',
      'no-session': 'The student must start their session before you can observe it.',
      'session-expired': 'Please close this tab and log in again to start a new session.',
      'incomplete-session-details': 'Session configuration is incomplete. Please contact support.',
      generic: 'Please close this tab and try again.',
    };
    return hints[this.errorCode ?? 'generic'] ?? 'Please close this tab and try again.';
  }

  get errorIcon(): string {
    const icons: Record<string, string> = {
      server: 'dns',
      building: 'hourglass_empty',
      'not-provisioned': 'build_circle',
      'no-session': 'power_off',
      'session-expired': 'lock_clock',
      'incomplete-session-details': 'info',
      generic: 'error_outline',
    };
    return icons[this.errorCode ?? 'generic'] ?? 'error_outline';
  }

  /**
   * Angular lifecycle - cleanup on component destroy
   */
  // ── Thumbnail capture helpers ─────────────────────────────────────────────

  private startThumbnailCapture(): void {
    this.clearThumbnailCapture();
    this.thumbnailIntervalId = setInterval(() => this.uploadThumbnail(), this.THUMBNAIL_INTERVAL_MS);
  }

  private clearThumbnailCapture(): void {
    if (this.thumbnailIntervalId != null) {
      clearInterval(this.thumbnailIntervalId);
      this.thumbnailIntervalId = null;
    }
  }

  // ── Focus-loss helpers ────────────────────────────────────────────────────
  //
  //  Single-phase design (Phase 2 auto-disconnect removed — crit_09):
  //
  //  Phase 1 — Silent detection (FOCUS_LOSS_DETECTION_MS, default 5–12 s).
  //    A single setTimeout fires after the user has been away long enough.
  //    No modal is shown yet, so brief glances away don't disturb the user.
  //    If focus returns before the timer fires it is cancelled quietly.
  //
  //  Modal — Indefinite Quit / Reconnect.
  //    Once Phase 1 fires the modal appears and stays until the user acts.
  //    Returning focus while the modal is open dismisses it automatically.
  //    There is NO auto-disconnect countdown.  The user must choose.

  private startFocusLossTimer(): void {
    // Skip: not connected, dialog already showing, or test-mode manual-form sessions.
    if (!this.isConnected || this.focusLossDialogOpen || this.isTestMode) return;
    // Skip: Phase 1 is already counting — double-fire guard.
    if (this.focusLossPhase1TimerId != null) return;

    this.logger.d(`Focus lost — waiting ${this.FOCUS_LOSS_DETECTION_MS / 1000}s before showing disconnect modal`);

    this.focusLossPhase1TimerId = setTimeout(
      () => this.showFocusLossWarning(),
      this.FOCUS_LOSS_DETECTION_MS
    );
  }

  private clearFocusLossTimer(): void {
    if (this.focusLossPhase1TimerId != null) {
      clearTimeout(this.focusLossPhase1TimerId);
      this.focusLossPhase1TimerId = null;
    }
  }

  /** Called by Phase 1 timeout — opens a Material confirmation dialog with Quit / Reconnect. */
  private showFocusLossWarning(): void {
    this.focusLossPhase1TimerId = null;
    this.focusLossDialogOpen = true;
    this.logger.d('Focus still lost — opening dialog (Quit / Reconnect, no auto-disconnect)');
    // Phase 2 auto-disconnect intentionally removed.
    this.confirmDialogService.confirm({
      title: 'Session Paused',
      message: 'You navigated away from your VM session. Your session is still active — return to this window to continue.',
      type: 'warning',
      confirmText: 'Reconnect',
      cancelText: 'Quit',
      disableClose: true,
      blurBackdrop: true,
      cancelLoadingDurationMs: 2000,
    }).subscribe(reconnect => {
      this.focusLossDialogOpen = false;
      if (reconnect) this.onFocusLossReconnect();
      else this.onFocusLossQuit();
    });
  }

  private cancelFocusLossTimer(): void {
    // Only cancel if Phase 1 is still ticking (user came back quickly)
    if (this.focusLossPhase1TimerId != null) {
      clearTimeout(this.focusLossPhase1TimerId);
      this.focusLossPhase1TimerId = null;
      this.logger.d('Focus restored during silent detection phase — phase 1 cancelled');
    }
    // DO NOT auto-dismiss the dialog here. Force the user to click Reconnect.
  }

  async onFocusLossReconnect(): Promise<void> {
    this.logger.i('User initiated manual reconnect from focus-loss dialog');

    if (!this.contextKey) {
      // No SHA key available — cannot re-fetch from backend.
      this.errorCode = 'session-expired';
      this.connectionError = 'Session context unavailable. Please close this window and relaunch.';
      this.focusLossDialogOpen = false;
      return;
    }

    // Terminate the current tunnel/session first so backend dedup does not
    // see this tab as still active during the next prepare-launch call.
    this.markTemplateSessionInactive();
    try { this.disconnectActiveClient(); } catch { /* best-effort */ }

    // Reset connection state
    this.isConnected = false;
    this.isConnecting = false;
    this.isContentReady = false;
    this.focusLossDialogOpen = false;
    this.connectionError = null;
    this.errorCode = null;

    // Clear the display container to wipe the frozen canvas
    const display = document.getElementById('display');
    if (display) {
      while (display.firstChild) {
        display.removeChild(display.firstChild);
      }
    }

    await this.showPreparingVmOverlay();

    // Always re-fetch credentials from backend — never read from localStorage cache.
    await this.connectViaContextKey(this.contextKey);
  }

  onFocusLossQuit(): void {
    this.performQuit();
  }

  private uploadThumbnail(): void {
    // Thumbnail capture is only supported for participant-self-vm sessions.
    if (this.thumbnailContext !== 'participant-self-vm') return;

    if (!this.thumbnailClassId || !this.thumbnailUserId || !this.thumbnailContext) return;

    const [classId, userId] = [this.thumbnailClassId, this.thumbnailUserId];
    const instance = this.vmConnectionService.getClientInstance(classId, userId);
    if (!instance) return;

    const thumbnail = instance.captureThumbnail();
    if (!thumbnail) return;

    this.classesService.upsertSessionThumbnail({
      classId: this.thumbnailClassId,
      userId: this.thumbnailUserId,
      context: this.thumbnailContext,
      thumbnailData: thumbnail,
    }).subscribe({
      error: (err: any) => {
        // 404 = session record TTL'd out or was not yet registered — expected on Safari
        // where the WebSocket can be killed before the component learns about it.
        // Stop the capture interval to avoid flooding dead sessions.
        if (err?.status === 404) {
          this.logger.d('Thumbnail upload skipped — session not found (404), stopping capture');
          this.clearThumbnailCapture();
        } else {
          this.logger.w('Thumbnail upload failed:', err);
        }
      },
    });
  }

  // ─────────────────────────────────────────────────────────────────────────────

  // ── Trainer monitoring indicator ─────────────────────────────────────────────

  /**
   * Subscribe to SignalR monitoring events pushed by the backend when a trainer
   * joins or leaves a participant's Guacamole session.  Only called for
   * participant / student contexts.
   */
  private subscribeToMonitoringEvents(classId: string): void {
    const startedSub = this.notificationsService.getMonitoringStartedStream().subscribe(payload => {
      if (payload?.classId === classId) {
        this.logger.i('Trainer joined session — showing monitoring indicator');
        this.isBeingMonitored = true;
      }
    });

    const stoppedSub = this.notificationsService.getMonitoringStoppedStream().subscribe(payload => {
      if (payload?.classId === classId) {
        this.logger.i('Trainer left session — hiding monitoring indicator');
        this.isBeingMonitored = false;
      }
    });

    this.monitoringSubscriptions.push(startedSub, stoppedSub);
  }

  private clearMonitoringSubscriptions(): void {
    this.monitoringSubscriptions.forEach(s => s.unsubscribe());
    this.monitoringSubscriptions = [];
  }

  // ─────────────────────────────────────────────────────────────────────────────

  ngOnDestroy(): void {
    this.clearThumbnailCapture();
    this.clearFocusLossTimer();
    this.clearMonitoringSubscriptions();
    this.logger.i('VM Component destroying...');

    // Remove beforeunload and unload handlers
    if (typeof window !== 'undefined') {
      window.removeEventListener('beforeunload', this.handleBeforeUnload);
      window.removeEventListener('unload', this.handleUnload);
    }

    if (this.isConnected) {
      this.disconnect();
    }

    // Clean up hold interval
    this.stopHoldingKey();
    this.removeOutsideClickListener();
    this.removeWindowEventListeners();
    if (this.resizeDebounceTimer) clearTimeout(this.resizeDebounceTimer);
  }

  /**
   * Copy text to clipboard
   */
  async copyToClipboard(text: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(text);
      this.logger.i('Copied to clipboard:', text);
      // Could add a toast notification here
    } catch (error) {
      this.logger.e('Failed to copy to clipboard:', error);
    }
  }

  /**
   * Send username keystrokes to VM
   */
  sendUsernameKeystrokes(): void {
    if (this.isSendingKeystrokes) {
      this.logger.i('Keystrokes already in progress, ignoring username request');
      return;
    }
    this.sendKeystrokes(this.connectionForm.username);
  }

  /**
   * Send password keystrokes to VM
   */
  sendPasswordKeystrokes(): void {
    if (this.isSendingKeystrokes) {
      this.logger.i('Keystrokes already in progress, ignoring password request');
      return;
    }
    this.sendKeystrokes(this.connectionForm.password);
  }

  // ---------------------------------------------------------------------------
  // AAD credential helpers
  // ---------------------------------------------------------------------------

  /** Template-accessible class ID for the AAD status card. */
  get aadClassId(): string | null { return this._guacClassId; }
  /** Template-accessible participant ID for the AAD status card. */
  get aadParticipantId(): string | null { return this._guacUserId; }
  /** Template-accessible UA template ID for the AAD status card. */
  get aadUaTemplateId(): string | null { return this._uaTemplateId; }
  /** Template-accessible launch context for the AAD status card. */
  get aadLaunchContext(): LaunchContext { return this._launchContext; }

  /** Toggle visibility of the Azure AD Temporary Access Pass. */
  toggleAadTap(): void {
    this.aadTapRevealed = !this.aadTapRevealed;
  }

  /** Toggle visibility of the VM Account password. */
  toggleVmPassword(): void {
    this.vmPasswordRevealed = !this.vmPasswordRevealed;
  }

  /** Copy the AAD Temporary Access Pass to the clipboard. */
  copyAadTap(): void {
    if (this.aadTemporaryAccessPass) {
      navigator.clipboard.writeText(this.aadTemporaryAccessPass);
    }
  }

  /** Copy the AAD User Principal Name to the clipboard. */
  copyAadUpn(): void {
    if (this.aadUserPrincipalName) {
      navigator.clipboard.writeText(this.aadUserPrincipalName);
    }
  }

  /** Injects `https://portal.azure.com` as keystrokes into the focused VM window. */
  sendPortalLinkKeystrokes(): void {
    if (this.isSendingKeystrokes) {
      this.logger.i('Keystrokes already in progress, ignoring portal link request');
      return;
    }

    this.sendKeystrokes(this.AZURE_PORTAL_URL);
  }

  /** Opens the Azure portal in a new browser tab. */
  openPortalInNewTab(): void {
    window.open(this.AZURE_PORTAL_URL, '_blank', 'noopener noreferrer');
  }

  /** Injects the AAD UPN as keystrokes into the focused VM window. */
  sendAadUpnKeystrokes(): void {
    if (this.isSendingKeystrokes || !this.aadUserPrincipalName) {
      this.logger.i('Keystrokes already in progress or UPN not available, ignoring UPN request');
      return;
    }

    this.sendKeystrokes(this.aadUserPrincipalName);
  }

  /** Injects the AAD TAP as keystrokes into the focused VM window. */
  sendAadTapKeystrokes(): void {
    if (this.isSendingKeystrokes || !this.aadTemporaryAccessPass) {
      this.logger.i('Keystrokes already in progress or TAP not available, ignoring TAP request');
      return;
    }

    this.sendKeystrokes(this.aadTemporaryAccessPass);
  }

  /**
   * Called by the VmAadStatusCardComponent when polling resolves credentials.
   * Updates the parent AAD credential fields so the active credentials block renders.
   */
  onAadCredentialsReady(creds: { userPrincipalName: string; temporaryAccessPass: string }): void {
    this.aadUserPrincipalName = creds.userPrincipalName;
    this.aadTemporaryAccessPass = creds.temporaryAccessPass;
    this.hasAadCredentials = true;
  }

  /**
   * Regenerates the TAP for the cloud user account.
   * Uses a context-dispatch object to route to the correct backend endpoint.
   * Prompts for confirmation then POSTs to the regenerate-tap endpoint.
   */
  regenerateTap(): void {
    if (!this._guacClassId || !this._guacUserId) return;

    const classId = this._guacClassId;
    const participantId = this.aadUserPrincipalName?.split('@')[0] ?? this._guacUserId;

    this.confirmDialogService.confirm({
      title: 'Regenerate Temporary Access Pass',
      message: 'This will invalidate the current TAP and generate a new one. The new TAP will be displayed immediately.',
      confirmText: 'Regenerate TAP',
      cancelText: 'Cancel',
    }).subscribe(confirmed => {
      if (!confirmed) return;

      const tapDispatch: Record<LaunchContext, () => Observable<{ temporaryAccessPass: string }>> = {
        'participant-self-vm': () => this.classesService.regenerateParticipantTap(classId, participantId),
        'operator-remote-vm': () => this.classesService.regenerateParticipantTap(classId, this._guacUserId!),
        'class-template-vm': () => this._uaTemplateId
          ? this.orgTemplateAadService.regenerateClassTemplateTap(this._uaTemplateId, classId)
          : EMPTY,
        'org-template-vm': () => EMPTY,
      };

      this.isRegeneratingTap = true;
      tapDispatch[this._launchContext]().subscribe({
        next: (res) => {
          this.aadTemporaryAccessPass = res.temporaryAccessPass;
          this.isRegeneratingTap = false;
        },
        error: (err: any) => {
          this.isRegeneratingTap = false;
          this.logger.e('Regenerate TAP error:', err);
        },
      });
    });
  }

  /**
   * Send Ctrl+Alt+Del to VM (for Windows lock screen)
   */
  sendCtrlAltDel(): void {
    // Ctrl+Alt+Del: Left Ctrl (0xFFE3) + Left Alt (0xFFE9) + Del (0xFFFF)
    // This is a critical sequence - disable all other keys during operation
    const ctrl = this.KEYSYM_MAP['ctrl'];
    const alt = this.KEYSYM_MAP['alt'];
    const del = this.KEYSYM_MAP['delete'];
    this.sendKeysyms(ctrl.number, alt.number, del.number, { sequence: 'combo' });
  }

  /**
   * Send Enter key to VM
   */
  sendEnterKey(): void {
    if (!this.isConnected) {
      this.logger.w('Cannot send Enter: VM not connected');
      return;
    }

    try {
      const clientInstance = this.getCurrentClientInstance();
      if (!clientInstance) {
        this.logger.e('No active Guacamole client found');
        return;
      }

      this.logger.i('Sending Enter key to VM');
      // Send Enter key (Return)
      const enter = this.KEYSYM_MAP['enter'];
      clientInstance.sendKeyEvent(1, enter.number); // Key down
      setTimeout(() => {
        clientInstance.sendKeyEvent(0, enter.number); // Key up
      }, 50);
    } catch (error) {
      this.logger.e('Failed to send Enter key:', error);
    }
  }

  /**
   * Send Tab key to VM
   */
  sendTabKey(): void {
    if (!this.isConnected) {
      this.logger.w('Cannot send Tab: VM not connected');
      return;
    }

    try {
      const clientInstance = this.getCurrentClientInstance();
      if (!clientInstance) {
        this.logger.e('No active Guacamole client found');
        return;
      }

      this.logger.i('Sending Tab key to VM');
      // Send Tab key
      const tab = this.KEYSYM_MAP['tab'] ?? this.KEYSYM_MAP['\t'];
      clientInstance.sendKeyEvent(1, tab.number); // Key down
      setTimeout(() => {
        clientInstance.sendKeyEvent(0, tab.number); // Key up
      }, 50);
    } catch (error) {
      this.logger.e('Failed to send Tab key:', error);
    }
  }

  /**
   * Send Backspace key to VM
   */
  sendBackspaceKey(): void {
    if (!this.isConnected) {
      this.logger.w('Cannot send Backspace: VM not connected');
      return;
    }

    try {
      const clientInstance = this.getCurrentClientInstance();
      if (!clientInstance) {
        this.logger.e('No active Guacamole client found');
        return;
      }

      this.logger.i('Sending Backspace key to VM');
      // Send Backspace key (0xFF08)
      const backspace = this.KEYSYM_MAP['backspace'];
      clientInstance.sendKeyEvent(1, backspace.number); // Key down
      setTimeout(() => {
        clientInstance.sendKeyEvent(0, backspace.number); // Key up
      }, 50);
    } catch (error) {
      this.logger.e('Failed to send Backspace key:', error);
    }
  }

  /**
   * Send Escape key to VM
   */
  sendEscapeKey(): void {
    const esc = this.KEYSYM_MAP['esc'];
    this.sendSimpleKey(esc.number);
  }

  /**
   * Start holding a key (for backspace and space)
   */
  startHoldingKey(keyType: string): void {
    if (!this.isConnected) {
      this.logger.w(`Cannot hold ${keyType}: VM not connected`);
      return;
    }

    // Stop any existing hold
    this.stopHoldingKey();

    this.heldKey = keyType;

    // Get keysym for the key type
    const key = this.KEYSYM_MAP[keyType];
    if (!key) return;

    // Send the first key immediately - no queue management for hold operations
    this.sendSimpleKey(key.number, false);

    // Set up interval for repeated key sending
    this.holdInterval = setInterval(() => {
      this.sendSimpleKey(key.number, false); // Simple key events, no interference with other operations
    }, 100); // Repeat every 100ms while holding
  }

  /**
   * Stop holding the current key
   */
  stopHoldingKey(): void {
    if (this.holdInterval) {
      clearInterval(this.holdInterval);
      this.holdInterval = null;
    }
    this.heldKey = null;
  }

  /**
   * Send individual Ctrl key to VM
   */
  sendCtrlKey(): void {
    // Left Ctrl key: 0xFFE3
    this.sendSimpleKey(0xFFE3);
  }

  /**
   * Send individual Alt key to VM
   */
  sendAltKey(): void {
    // Left Alt key: 0xFFE9
    this.sendSimpleKey(0xFFE9);
  }

  /**
   * Send individual Delete key to VM
   */
  sendDelKey(): void {
    // Delete key: 0xFFFF
    this.sendSimpleKey(0xFFFF);
  }


  /**
   * Get current Guacamole client instance.
   *
   * Uses _guacClassId / _guacUserId which are set in connectWithData and
   * correctly identify the active session regardless of the sessionKey format
   * (the SHA-keyed "org-template-{userId}-{templateId}" format cannot be
   * reliably split by '-' to recover the original classId and userId).
   */
  private getCurrentClientInstance(): any {
    if (this.isTestMode) {
      return this.vmConnectionService.getCurrentClient();
    } else if (this._guacClassId && this._guacUserId) {
      return this.vmConnectionService.getCurrentClient(this._guacClassId, this._guacUserId);
    }
    return null;
  }

  /**
   * Send simple single key - can optionally disable all other keys during operation
   * @param keysym - The keysym to send
   * @param disableOthers - Whether to disable all other keys during this operation (default: false)
   */
  private sendSimpleKey(keysym: number, disableOthers: boolean = false): void {
    if (!this.isConnected) {
      this.logger.w('Cannot send key: VM not connected');
      return;
    }

    if (disableOthers && this.isSendingKeystrokes) {
      this.logger.w('Keystrokes already in progress');
      return;
    }

    try {
      const clientInstance = this.getCurrentClientInstance();
      if (!clientInstance) {
        this.logger.e('No active Guacamole client found');
        return;
      }

      if (disableOthers) {
        this.isSendingKeystrokes = true;
      }

      this.logger.i(`Sending simple key: 0x${keysym.toString(16)}`);
      clientInstance.sendKeyEvent(1, keysym); // Key down
      setTimeout(() => {
        clientInstance.sendKeyEvent(0, keysym); // Key up
        if (disableOthers) {
          this.isSendingKeystrokes = false; // Re-enable all keys
        }
      }, 50);
    } catch (error) {
      this.logger.e('Failed to send simple key:', error);
      if (disableOthers) {
        this.isSendingKeystrokes = false; // Re-enable on error
      }
    }
  }

  /**
   * Common method to send keysyms directly to VM with queue management
   * For complex operations that send multiple strokes (like text input, combos)
   *
   * @param keysyms - One or more keysym values to send (supports key combinations)
   * @param options - Optional configuration { hold?: boolean, sequence?: 'press'|'combo' }
   *
   * Examples:
   * - Single key: sendKeysyms(0x0041) // 'A' key
   * - Key combo: sendKeysyms(0xFFE3, 0xFFE9, 0xFFFF) // Ctrl+Alt+Del
   * - Hold key: sendKeysyms(0x0020, { hold: true }) // Hold space
   */
  private sendKeysyms(...args: (number | { hold?: boolean; sequence?: 'press' | 'combo' })[]): void {
    if (!this.isConnected) {
      this.logger.w('Cannot send keysyms: VM not connected');
      return;
    }

    if (this.isSendingKeystrokes) {
      this.logger.w('Keystrokes already in progress');
      return;
    }

    // Parse arguments - last arg might be options object
    const lastArg = args[args.length - 1];
    const options = (typeof lastArg === 'object' && !Array.isArray(lastArg)) ? lastArg as any : {};
    const keysyms = (typeof lastArg === 'object' && !Array.isArray(lastArg))
      ? args.slice(0, -1) as number[]
      : args as number[];

    try {
      const clientInstance = this.getCurrentClientInstance();
      if (!clientInstance) {
        this.logger.e('No active Guacamole client found');
        return;
      }

      this.isSendingKeystrokes = true;
      this.logger.i('Sending keysyms to VM:', keysyms.map(k => `0x${k.toString(16)}`).join(', '));

      if (options.sequence === 'combo' || keysyms.length > 1) {
        // Send key combination (press all keys, then release in reverse order)
        this.sendKeyCombo(clientInstance, keysyms);
      } else {
        // Send single key press
        this.sendSingleKey(clientInstance, keysyms[0], options.hold || false);
      }

    } catch (error) {
      this.logger.e('Failed to send keysyms:', error);
      this.isSendingKeystrokes = false;
    }
  }

  /**
   * Send single key press/release
   */
  private sendSingleKey(client: any, keysym: number, hold: boolean = false): void {
    client.sendKeyEvent(1, keysym); // Key down

    if (!hold) {
      setTimeout(() => {
        client.sendKeyEvent(0, keysym); // Key up
        this.isSendingKeystrokes = false;
      }, 50);
    } else {
      // For hold keys, don't release automatically - handled by stopHoldingKey()
      this.isSendingKeystrokes = false;
    }
  }

  /**
   * Send key combination (like Ctrl+Alt+Del)
   */
  private sendKeyCombo(client: any, keysyms: number[]): void {
    let delay = 0;

    // Press all keys in sequence
    keysyms.forEach((keysym, index) => {
      setTimeout(() => {
        client.sendKeyEvent(1, keysym);
        this.logger.i(`Pressed key: 0x${keysym.toString(16)}`);
      }, delay);
      delay += 50;
    });

    // Release all keys in reverse order
    keysyms.slice().reverse().forEach((keysym, index) => {
      setTimeout(() => {
        client.sendKeyEvent(0, keysym);
        this.logger.i(`Released key: 0x${keysym.toString(16)}`);

        // Clear queue state after last key release
        if (index === keysyms.length - 1) {
          this.isSendingKeystrokes = false;
        }
      }, delay);
      delay += 50;
    });
  }

  /**
   * Send keystrokes to VM via Guacamole client (works for both test and production)
   */
  private sendKeystrokes(text: string): void {
    if (!this.isConnected) {
      this.logger.w('Cannot send keystrokes: VM not connected');
      return;
    }

    if (this.isSendingKeystrokes) {
      this.logger.w('Keystrokes already in progress');
      return;
    }

    try {
      const clientInstance = this.getCurrentClientInstance();
      if (!clientInstance) {
        this.logger.e('No active Guacamole client found');
        return;
      }

      this.isSendingKeystrokes = true;
      this.logger.i('Sending keystrokes to VM:', text);
      this.sendStringToGuacamole(clientInstance, text);
    } catch (error) {
      this.logger.e('Failed to send keystrokes:', error);
      this.isSendingKeystrokes = false;
    }
  }

  /**
   * Convert character to X11 keysym with proper mapping
   */
  private getKeysymFromChar(char: string): { keysym: number, needsShift: boolean } {
    const charCode = char.charCodeAt(0);

    // DEBUG: Log character processing
    this.logger.i(`DEBUG: Processing char '${char}' (charCode: ${charCode})`);

    // Alphanumeric characters (a-z, A-Z, 0-9) don't need shift
    if (/^[a-zA-Z0-9]$/.test(char)) {
      const result = { keysym: charCode, needsShift: false };
      this.logger.i(`DEBUG: Alphanumeric '${char}' -> direct keysym: ${result.keysym} (0x${result.keysym.toString(16)}), no shift needed`);
      return result;
    }

    // Check if character is in the KEYSYM_MAP
    const mapped = this.KEYSYM_MAP[char];
    if (mapped) {
      this.logger.i(`DEBUG: Mapped '${char}' -> keysym: ${mapped.number} (0x${mapped.number.toString(16)}), needsShift: ${mapped.needsShift}`);
      return { keysym: mapped.number, needsShift: mapped.needsShift };
    }

    // For any other character, return its char code directly (may not work for all)
    return { keysym: charCode, needsShift: false };
  }

  /**
   * Send character to Guacamole client with proper shift handling
   */
  private sendCharacter(client: any, char: string): void {
    const charInfo = this.getKeysymFromChar(char);
    this.logger.i(`DEBUG: Sending char '${char}' -> keysym: 0x${charInfo.keysym.toString(16)}, needsShift: ${charInfo.needsShift}`);

    if (charInfo.keysym) {
      if (charInfo.needsShift) {
        this.logger.i(`DEBUG: Sending uppercase '${char}' with shift modifier`);
        // Hold Shift (0xFFE1 = Left Shift) - increase timing for reliability
        client.sendKeyEvent(1, 0xFFE1);
        setTimeout(() => {
          // Send character keydown
          client.sendKeyEvent(1, charInfo.keysym);
          setTimeout(() => {
            // Send character keyup
            client.sendKeyEvent(0, charInfo.keysym);
            setTimeout(() => {
              // Release Shift
              client.sendKeyEvent(0, 0xFFE1);
            }, 50);
          }, 50);
        }, 50);
      } else {
        // Send character without shift
        client.sendKeyEvent(1, charInfo.keysym);
        setTimeout(() => {
          client.sendKeyEvent(0, charInfo.keysym);
        }, 25);
      }
    }
  }

  /**
   * Send complete string to Guacamole client with timing
   */
  private sendStringToGuacamole(client: any, text: string, delay: number = 60): void {
    this.logger.i(`Sending string to VM: "${text}" (${text.length} characters)`);

    // Ensure delay is at least 10ms
    delay = Math.max(10, delay);

    let index = 0;

    const sendNextChar = () => {
      if (index < text.length) {
        const char = text.charAt(index);
        const charInfo = this.getKeysymFromChar(char);
        this.logger.i(`Sending char "${char}" (index: ${index}, keysym: 0x${charInfo.keysym.toString(16)}, shift: ${charInfo.needsShift})`);

        this.sendCharacter(client, char);
        index++;
        // Longer delay to accommodate shift key sequences - increased for reliability
        const charDelay = charInfo.needsShift ? delay + 50 : delay;
        setTimeout(sendNextChar, charDelay);
      } else {
        this.logger.i('Finished sending string to VM');
        this.isSendingKeystrokes = false; // Re-enable keystroke buttons
      }
    };

    sendNextChar();
  }

  /**
   * Calculate optimal resolution for the remote display.
   *
   * Two competing concerns are balanced here:
   *   1. The remote VM needs a sane minimum resolution to be usable (Windows starts to
   *      collapse Start menu / Action Center widgets below ~800x480).
   *   2. On a small browser window (laptop in a tiled split, or a mobile viewport) we
   *      do NOT want to ask for a 1024-wide canvas and then have it overflow with
   *      scrollbars. The canvas size we request from guacd is independent of how it's
   *      rendered locally, so we then call {@link scaleDisplayToViewport} to scale the
   *      rendered display down to fit. The remote VM still receives a comfortable
   *      desktop and the user sees it within their viewport without scrollbars.
   */
  private calculateOptimalResolution(): void {
    // Capture system resolution
    this.systemResolution = {
      width: window.innerWidth,
      height: window.innerHeight
    };

    // Small but safe margins for browser chrome
    const marginWidth = 40;
    const marginHeight = 60;

    // Floor is 800×480 rather than the previous 1024×576 so that small laptop
    // windows, tablet viewports, and tiled split-screen layouts still receive a
    // comfortable canvas without triggering horizontal scrollbars (the
    // `scaleDisplayToViewport` helper further shrinks the *rendered* display to fit
    // the actual viewport without changing the resolution sent to guacd). Ceiling
    // is 2560×1440 to keep guacd memory bounded for very large monitors. The actual
    // requested canvas is the viewport size minus chrome margins, clamped to that range.
    this.optimalResolution = {
      width: Math.max(800, Math.min(2560, this.systemResolution.width - marginWidth)),
      height: Math.max(480, Math.min(1440, this.systemResolution.height - marginHeight))
    };

    this.logger.i(`System: ${this.systemResolution.width}x${this.systemResolution.height}, Computed: ${this.optimalResolution.width}x${this.optimalResolution.height}`);
  }

  /**
   * Apply a CSS scale to the Guacamole display so a canvas larger than the available
   * viewport is shown shrunk-to-fit (no horizontal scrollbar) and a canvas smaller than
   * the viewport stays at native scale.
   *
   * Uses the underlying `Guacamole.Display.scale(s)` method which is the supported way
   * to resize the rendered output without changing the resolution requested from guacd.
   * Called once on first content render and then on every debounced window resize so the
   * scaling tracks the viewport.
   */
  private scaleDisplayToViewport(): void {
    if (!this._guacClassId || !this._guacUserId) return;
    const instance = this.vmConnectionService.getClientInstance(this._guacClassId, this._guacUserId);
    if (!instance) return;
    const guac = instance.getGuacamoleClient();
    if (!guac) return;

    let display: any;
    try { display = guac.getDisplay(); }
    catch { return; }
    if (!display) return;

    let nativeWidth: number;
    let nativeHeight: number;
    try {
      nativeWidth = typeof display.getWidth === 'function' ? display.getWidth() : 0;
      nativeHeight = typeof display.getHeight === 'function' ? display.getHeight() : 0;
    } catch { return; }
    if (!nativeWidth || !nativeHeight) return;

    // Account for the same chrome margins as the requested resolution; no need to
    // reserve space for the side drawer (it's an overlay layer).
    const availWidth = Math.max(1, window.innerWidth - 16);
    const availHeight = Math.max(1, window.innerHeight - 16);

    const scale = Math.min(1, availWidth / nativeWidth, availHeight / nativeHeight);
    try {
      display.scale(scale);
      this.logger.d(`Display scaled to ${Math.round(scale * 100)}% (native ${nativeWidth}x${nativeHeight}, viewport ${availWidth}x${availHeight}).`);
    } catch (err) {
      this.logger.w('Display scale failed:', err);
    }
  }

  // ---------------------------------------------------------------------------
  // Connection health: callbacks, visibility guard, and RDP resize
  // ---------------------------------------------------------------------------

  private showFileTransferNotAllowedSnackbar(): void {
    this.snackBar.open(`⚠ File transfer is not allowed for this VM`, 'Dismiss', {
      duration: 10000,
      panelClass: ['snackbar-warning'],
      horizontalPosition: 'end',
      verticalPosition: 'top',
    });
  }

  /**
   * Attach reconnect lifecycle callbacks to the GuacamoleClientInstance so that
   * the component can update its UI (reconnecting banner, error message, etc.)
   * without polling.
   */
  private setupConnectionCallbacks(classId: string, userId: string): void {
    const instance = this.vmConnectionService.getClientInstance(classId, userId);
    if (!instance) return;

    instance.onReconnecting = (attempt: number, maxAttempts: number) => {
      this.isReconnecting = true;
      this.reconnectAttemptDisplay = attempt;
      this.reconnectMaxAttempts = maxAttempts;
      this.isContentReady = false;
      this.logger.i(`Reconnecting: attempt ${attempt}/${maxAttempts}`);
    };

    instance.onReconnected = () => {
      this.isReconnecting = false;
      this.reconnectAttemptDisplay = 0;
      this.isContentReady = false;
      this.logger.i('Reconnected to VM — waiting for content to render');
      this.waitForContentToRender().then(() => {
        this.isContentReady = true;
        // Re-fit the display to the current viewport after reconnect, since the
        // canvas element was recreated by the new client.
        this.scaleDisplayToViewport();
      });
    };

    instance.onConnectionFailed = () => {
      this.isReconnecting = false;
      this.isConnected = false;
      this.connectionError =
        'The connection to this VM has been lost and could not be restored. ' +
        'Please close this window and relaunch the VM from your class portal.';
      this.logger.e('VM connection permanently lost after max reconnect attempts');
    };

    // ── File transfer notifications ──────────────────────────────────────────

    if (!this.isFileTransferAllowed) {
      this.logger.i('Template does not allow file transfer — setting up drag-and-drop listeners to show warnings');

      instance.isFileTransferAllowed = false;
      const logWarningAndShowSnackbar = (action: string, filename: string) => {
        this.logger.d(`'${action}' attempted but file transfer is not allowed by template capabilities. Filename: "${filename}"`);
        this.showFileTransferNotAllowedSnackbar();
      };

      instance.dragOverListener = (event: DragEvent) => {
        event.preventDefault();
        event.stopPropagation();
        event.dataTransfer!.effectAllowed = 'copy';
        event.dataTransfer!.dropEffect = 'copy';
        logWarningAndShowSnackbar('File drag-and-drop', 'N/A');
      };

      instance.dropListener = (event: DragEvent) => {
        event.preventDefault();
        event.stopPropagation();
        logWarningAndShowSnackbar('File drop', 'N/A');
      };

      if (instance.client) {
        instance.client.onfile = (stream: InputStream, mimetype: string, filename: string) => {
          // Reject the incoming file with CLIENT_UNAUTHORIZED status code
          stream.sendAck('Reject', Guacamole.Status.Code.CLIENT_UNAUTHORIZED);
          logWarningAndShowSnackbar('File download from VM', filename);
        };
      }

      instance.onFileUploadStart = (filename: string, _sizeBytes: number) => {
        logWarningAndShowSnackbar('File upload start', filename);
      };

      instance.onFileUploadComplete = (filename: string) => {
        logWarningAndShowSnackbar('File upload complete', filename);
      };

      instance.onFileUploadError = (filename: string, _err: any) => {
        logWarningAndShowSnackbar('File upload error', filename);
      };

      instance.onFileDownloadStart = (filename: string, length: number) => {
        logWarningAndShowSnackbar('File download start', filename);
      };

      instance.onFileDownloadComplete = (filename: string) => {
        logWarningAndShowSnackbar('File download complete', filename);
      };

      return; // Don't set up upload/download callbacks if file transfer isn't allowed
    } else {
      this.logger.d('File transfer is allowed — setting up upload/download callbacks for notifications');
      instance.isFileTransferAllowed = true;
    }

    // Show a brief snackbar at top-right for both upload and download events
    // so the notification is noticeable without blocking the VM viewport.
    instance.onFileUploadStart = (filename: string, _sizeBytes: number) => {
      this.snackBar.open(`⬆ Uploading "${filename}" to VM…`, 'OK', {
        duration: 5000,
        panelClass: ['snackbar-info'],
        horizontalPosition: 'end',
        verticalPosition: 'top',
      });
    };

    instance.onFileUploadComplete = (filename: string) => {
      this.snackBar.open(`✓ "${filename}" uploaded to VM`, 'OK', {
        duration: 5000,
        panelClass: ['snackbar-success'],
        horizontalPosition: 'end',
        verticalPosition: 'top',
      });
    };

    instance.onFileUploadError = (filename: string, _err: any) => {
      this.snackBar.open(`✕ Upload failed for "${filename}"`, 'Dismiss', {
        duration: 10000,
        panelClass: ['snackbar-error'],
        horizontalPosition: 'end',
        verticalPosition: 'top',
      });
    };
    const downloadProgressMap: Map<string, {
      downloadedMB$: BehaviorSubject<string>;
      accumulatedBytes: number;
      snackbarRef: MatSnackBarRef<DownloadSnackbarComponent>;
      intervalId: number;
    }> = new Map();

    const toMB = (bytes: number): string => ((bytes / (1024 * 1024)).toFixed(2));

    instance.onFileDownloadStart = (filename: string, length: number) => {
      if (!downloadProgressMap.has(filename)) {
        // Start at '0.00'
        const downloadedMB$ = new BehaviorSubject<string>('0.00');

        downloadProgressMap.set(filename, {
          accumulatedBytes: 0,
          downloadedMB$: downloadedMB$,
          snackbarRef: this.snackBar.openFromComponent(DownloadSnackbarComponent, {
            data: { filename: filename, downloadedMB$: downloadedMB$ }, // Pass separated data
            panelClass: ['snackbar-info'],
            horizontalPosition: 'end',
            verticalPosition: 'top',
          }),
          intervalId: window.setInterval(() => {
            const progress = downloadProgressMap.get(filename);
            if (progress) {
              // Only push the calculated numbers to the subject
              progress.downloadedMB$.next(toMB(progress.accumulatedBytes));
            }
          }, 1000),
        });
      }

      const progress = downloadProgressMap.get(filename);
      if (progress) {
        // Just increment bytes here. The interval handles the UI updates!
        progress.accumulatedBytes += length;
      }
    };

    instance.onFileDownloadComplete = (filename: string) => {
      const progress = downloadProgressMap.get(filename);
      if (progress) {
        clearInterval(progress.intervalId);
        progress.downloadedMB$.complete();
        progress.snackbarRef.dismiss();
        downloadProgressMap.delete(filename);
      }

      this.snackBar.open(`⬇ "${filename}" (${toMB(progress?.accumulatedBytes || 0)} MB) sent to browser Downloads`, 'OK', {
        duration: 10000,
        panelClass: ['snackbar-success'],
        horizontalPosition: 'end',
        verticalPosition: 'top',
      });
    };
  }

  /**
   * Listen for window resize events and forward updated dimensions to the remote
   * RDP session so that the remote display tracks the physical window size.
   * VNC does not support dynamic resize, so the handler is skipped for that
   * protocol.
   */
  private setupResizeHandler(): void {
    this.windowResizeHandler = () => {
      if (this.resizeDebounceTimer) clearTimeout(this.resizeDebounceTimer);
      this.resizeDebounceTimer = setTimeout(() => {
        if (!this.isConnected || !this._guacClassId || !this._guacUserId) return;

        // Re-fit the rendered display to the new viewport — covers both protocols and
        // also handles the small-screen case where the canvas is larger than the
        // available room (the user just shrank the window).
        this.scaleDisplayToViewport();

        // Dynamic resize is only meaningful for RDP — VNC has a fixed server-side
        // resolution and ignores `size` instructions.
        if (this.connectionForm.protocol !== 'rdp') return;

        this.calculateOptimalResolution();
        const instance = this.vmConnectionService.getClientInstance(this._guacClassId, this._guacUserId);
        if (instance) {
          instance.sendSize(this.optimalResolution.width, this.optimalResolution.height);
          this.logger.i(
            `Window resized — sent new RDP dimensions: ` +
            `${this.optimalResolution.width}x${this.optimalResolution.height}`
          );
        }
      }, this.RESIZE_DEBOUNCE_MS);
    };
    window.addEventListener('resize', this.windowResizeHandler);
  }

  /**
   * Handle the page becoming visible again (user switches back to this tab/window)
   * and the window regaining focus.  Browsers may silently drop WebSocket
   * connections during periods of inactivity in background tabs, so we probe
   * the client state and trigger a reconnect if the connection is gone.
   */
  private setupVisibilityHandlers(): void {
    this.visibilityChangeHandler = () => {
      if (document.visibilityState === 'hidden') {
        this.startFocusLossTimer();
      } else {
        // Only cancel the silent timer if they came back fast enough.
        // DO NOT automatically check the connection or auto-reconnect here.
        this.cancelFocusLossTimer();
      }
    };

    // Sync isFullscreen when the user exits fullscreen via Esc or browser chrome.
    document.addEventListener('fullscreenchange', () => {
      this.isFullscreen = !!document.fullscreenElement;
    });

    // Probe the clipboard-read permission so the drawer hides the "Request access"
    // button when the browser has already granted it (avoids redundant UI noise).
    this.refreshClipboardPermissionState();

    // Remove the windowFocusHandler completely to prevent silent auto-reconnects
    document.addEventListener('visibilitychange', this.visibilityChangeHandler);
  }

  /**
   * Probe the Guacamole client instance when the window comes back into view.
   * If the component believes it is connected but the underlying WebSocket is
   * gone, initiate a reconnect so the user sees a meaningful status instead of
   * a frozen last-frame.
   */
  private checkConnectionOnFocus(): void {
    if (!this._guacClassId || !this._guacUserId || !this.isConnected || this.isReconnecting) return;
    const instance = this.vmConnectionService.getClientInstance(this._guacClassId, this._guacUserId);
    if (!instance) return;

    if (!instance.isClientConnected()) {
      this.logger.w('Connection appears dropped after focus — triggering reconnect');
      this.isReconnecting = true;
      this.isContentReady = false;
      instance.triggerReconnect();
    }
  }

  /**
   * Remove all window-level event listeners registered by this component.
   * Safe to call multiple times.
   */
  private removeWindowEventListeners(): void {
    if (this.windowResizeHandler) {
      window.removeEventListener('resize', this.windowResizeHandler);
      this.windowResizeHandler = undefined;
    }

    if (this.visibilityChangeHandler) {
      document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
      this.visibilityChangeHandler = undefined;
    }

    // Note: windowFocusHandler cleanup has been removed entirely
    // since we are no longer using window.focus to trigger auto-reconnects.

    this.clearFocusLossTimer();
  }

  /**
   * Set up outside click listener to close sidebar
   */
  private setupOutsideClickListener(): void {
    this.outsideClickListener = (event: Event) => {
      const target = event.target as Element;
      const sidebar = document.querySelector('.account-sidebar');
      const toggleButton = document.querySelector('.floating-toggle-btn');

      // Close sidebar if clicked outside of sidebar and toggle button
      if (this.sidebarVisible && sidebar && toggleButton) {
        if (!sidebar.contains(target) && !toggleButton.contains(target)) {
          this.sidebarVisible = false;
        }
      }
    };

    document.addEventListener('click', this.outsideClickListener);
  }

  /**
   * Remove outside click listener
   */
  private removeOutsideClickListener(): void {
    if (this.outsideClickListener) {
      document.removeEventListener('click', this.outsideClickListener);
      this.outsideClickListener = undefined;
    }
  }

  /**
   * Wait for VM content to actually render in the display
   */
  private async waitForContentToRender(): Promise<void> {
    const maxWaitTime = 10000; // 10 seconds max
    const checkInterval = 200; // Check every 200ms
    const startTime = Date.now();

    return new Promise((resolve) => {
      const checkForContent = () => {
        const display = document.getElementById('display');
        const canvas = display?.querySelector('canvas');

        // Check if canvas exists and has some content
        if (canvas && canvas.width > 0 && canvas.height > 0) {
          // Additional delay to ensure content is fully rendered
          setTimeout(resolve, 500);
          return;
        }

        // Timeout after max wait time
        if (Date.now() - startTime > maxWaitTime) {
          this.logger.i('Content render timeout, proceeding anyway');
          resolve();
          return;
        }

        // Check again after interval
        setTimeout(checkForContent, checkInterval);
      };

      checkForContent();
    });
  }

}

